import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, tap, map } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Logger, ApiValidation, ConfirmDialogComponent, OrganizationService, MediaPreviewComponent } from 'src/app/@shared';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { Asset, AssetDomain, AssetGroup, AssetTag, AssetTagService, AssetsDomainService, AssetsService, AssetDetails } from 'src/app/modules/standard/v1';
import { COMMA, ENTER } from '@angular/cdk/keycodes';

const log = new Logger('AssetsEditDetailsComponent');

@Component({
  selector: 'app-assets-edit-details',
  templateUrl: './assets-edit-details.component.html',
  styleUrls: ['./assets-edit-details.component.scss'],
})

export class AssetsEditDetailsComponent<Tag extends AssetTag, Group extends AssetGroup> implements OnInit, OnDestroy {
  assets$: Observable<any> = of({} as any);
  assetsDetails!: AssetDetails;
  assetGroups$: any[] = [] as any[];
  assetTags$: Observable<Tag[]> = this.assetTagService.assetTags$.pipe(map(tags => {
    return tags.filter(tag => {
      const index = this.assetTagIds.findIndex(_tag => _tag.Id == tag.Id);
      return index < 0
    })
  }));
  assetsUrl$: Observable<string> = this.organizationService.assetsUrl$;
  tagCtrl = new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(50)]);
  assetTagIds: Tag[] = [];
  addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  formGroup = new FormGroup({
    // ParentOfferId: new FormControl<string | null>(''),
    detail: new FormGroup({
      clientKey: new FormControl<string | null>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100)]),
      assetName: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100)]),
      fileName: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100)]),
      assetGroup: new FormControl<string>({ value: '', disabled: true }, [Validators.minLength(1), Validators.maxLength(100)]),
      dateAdded: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(100)]),
      id: new FormControl<string>('0', { nonNullable: true })

    })
  });
  groupId: string = '0';
  assetId: string = '0';
  assetType: string = '';
  private assetTagsList: Tag[] = [];

  constructor(private router: Router,
    private route: ActivatedRoute,
    private assetDomainService: AssetsDomainService<AssetDomain>,
    private assetTagService: AssetTagService<Tag, Group>,
    private organizationService: OrganizationService,
    private matSnackBar: MatSnackBar,
    private assetsService: AssetsService<Asset>,
    private dialog: MatDialog) { }

  ngOnInit(): void {
    log.debug('init');
    this.assetTagService.reload();
    // get the asset id from the parent route
    this.assetId = this.route.snapshot.params['assetId'];
    this.assetTagService.assetGroups$.subscribe({
      next: (data) => {
        this.assetGroups$ = data;
      }
    });
    this.assetTagService.assetTags$.subscribe(assetTags => {
      this.assetTagsList = assetTags;
    })
    // if we are editing a record, get the record data
    if (this.assetId && this.assetId !== '0') {
      this.assets$ = this.assetDomainService.getAssetById(this.assetId).pipe(
        tap((asset: any) => {
          this.assetsDetails = asset.Detail;
          this.assetType = asset.Type;
          asset = this.toCamel(asset);
          asset.detail.assetGroup = asset?.assetGroup.id;
          this.formGroup.patchValue(asset);
          this.formGroup.markAllAsTouched();

          if (asset.assetTagIds && asset.assetTagIds.length) {
            this.assetTagService.getAssetTagsList().subscribe((tags: Tag[]) => {
              asset.assetTagIds?.forEach((id: any) => {
                const index = tags.findIndex(_tag => _tag.Id == id);
                if (index >= 0) {
                  this.assetTagIds.push(tags[index]);
                }
              });
            })
          }
        })
      );
    }
  }

  trimControlValues(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key: string) => {
      const abstractControl = formGroup.get(key);
      if (abstractControl instanceof FormGroup) {
        this.trimControlValues(abstractControl);
      } else {
        if (typeof abstractControl?.value == 'string') {
          abstractControl.setValue(abstractControl?.value.trim());
        }
      }
    })
  }


  save() {
    this.trimControlValues(this.formGroup);

    if (this.formGroup.valid) {
      let assets = this.formGroup.getRawValue() as any;
      assets.assetTagIds = this.assetTagIds.map(tag => tag.Id);
      const dataToPass = {
        file: assets.detail.file,
        assetTagIds: assets.assetTagIds,
        Type: this.assetType,
        Detail: {
          FileName: assets.detail?.fileName,
          AssetName: assets.detail?.assetName,
          DateAdded: assets.detail?.dateAdded,
          ClientKey: assets.detail?.clientKey,
          Type: this.assetType,
        },
        AssetGroup: {
          Id: assets.detail?.assetGroup
        }
      }
      // formData.append('file', assets.detail.file);
      // formData.append('assetTagIds', assets.assetTagIds as any);
      // if(assets.detail?.fileName){
      //   formData.append('Detail.FileName', assets.detail?.fileName);
      // }
      // if(assets.detail?.assetName){
      //   formData.append('Detail.AssetName', assets.detail?.assetName);
      // }
      // if(assets.detail?.dateAdded){
      //   formData.append('Detail.DateAdded', assets.detail?.dateAdded);
      // }
      // if(assets.detail?.clientKey){
      //   formData.append('Detail.ClientKey', assets.detail?.clientKey);
      // }

      // // formData.append('AssetGroup.AssetGroupName', assets.detail?.assetGroup?.AssetGroupName);
      // formData.append('AssetGroup.Id', assets.detail?.assetGroup);

      this.assetDomainService.updateAsset(dataToPass, this.assetId).subscribe({
        next: (response: any) => {

          this.matSnackBar.open(
            `${this.formGroup.controls.detail.controls.assetName.value} saved`, 'OK', DEFAULT_SNACKBAR_CONFIG
          );
          this.assetsService.search('');
          this.assetsService.reload();
          this.router.navigate([`../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
        },
        error: (error) => {
          if (error.status === 500) {
            log.error('500 Error saving asset', error);
            this.matSnackBar.open('500 Error saving asset', 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
          }
          if (error.status === 400) {
            const apiValidations: any = error.error;
            if (Array.isArray(apiValidations)) {
              apiValidations.forEach((validation: any) => {
                if (this.formGroup?.get(validation.PropertyName)) {
                  const control = this.formGroup?.get(validation.PropertyName);
                  if (control) {
                    control.markAsTouched();
                    control.setErrors({ invalid: validation.ErrorMessage });
                    this.matSnackBar.open(validation.ErrorMessage, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
                  }
                } else {
                  ///TODO: if we have cross field validation then show the validation error at the top of the screen
                  // if we have cross field validation then show the validation error at the top of the screen
                  // push general error messages to array this is displayed in a toast or dialog
                }
              });
            } else {
              this.matSnackBar.open(apiValidations, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
            }


          }
        }
      });
    }
  }

  reloadCurrentRoute() {
    let currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  cancel(): void {
    this.formGroup.reset();
    this.router.navigate([`../`], { relativeTo: this.route });

  }

  deleteRecord() {
    const record = this.formGroup.getRawValue();
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: `Are you sure you wish to delete ${record.detail.assetName}?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe(
      confirmResult => {
        if (confirmResult) {
          this.assetsService.deleteAsset(record?.detail.id).subscribe({
            next: () => {
              this.matSnackBar.open(`${record.detail.assetName} deleted`, 'OK', DEFAULT_SNACKBAR_CONFIG);
              this.assetsService.reload();
              this.router.navigate([`../`], { relativeTo: this.route });
            },
            error: (error) => {
              log.error('Error deleting asset', error);

              if (error.error.value) {
                throw new Error(error.error.value);
              } else {
                throw new Error(error.message);
              }
            }
          });
        }
      });
  }

  tagSelected(event: MatAutocompleteSelectedEvent): void {
    if (this.assetTagIds && this.assetTagIds.length > 0 && this.assetTagIds.includes(event.option.value)) {
      return;
    }
    this.assetTagIds.push(event.option.value);
    this.tagCtrl.setValue('');
    this.assetTags$ = this.assetTags$.pipe(map(tags => {
      return tags.filter(tag => {
        const index = this.assetTagIds.findIndex(_tag => _tag.Id == tag.Id);
        return index < 0
      })
    }));
  }

  addTag(event: MatChipInputEvent): void {
    event.chipInput!.clear();
    const value = (event.value || '').trim();
    let valid = this.tagCtrl.status !== "INVALID";
    // Add Tag
    if (value && valid) {
      let tag = <Tag>{
        ClientKey: value,
        AssetTagName: value,
      };

      this.saveTag(tag);
    }

    // Clear the input value
    event.chipInput!.clear();
    this.tagCtrl.setValue('');
  }

  saveTag(tag: Tag) {
    if (!this.assetTagsList) this.assetTagsList = [];
    const tagInfo = this.assetTagsList.find(x => x.AssetTagName && tag.AssetTagName && x.AssetTagName.toLowerCase() === tag.AssetTagName.toLowerCase());
    if (tagInfo) {
      if (!this.assetTagIds.find(x => x.AssetTagName == tagInfo.AssetTagName)) {
        this.assetTagIds.push(tagInfo);
      }
    }
    else {
      this.assetTagService.saveAssetTag(tag as Tag).subscribe({
        next: (response) => {
          this.assetTagIds.push(response as Tag);
          this.matSnackBar.open(
            `${tag.AssetTagName} saved`, 'OK', DEFAULT_SNACKBAR_CONFIG
          );
          this.assetTagService.reload();
        },
        error: (error) => {
          if (error.status === 500) {
            log.error('500 Error saving event', error);
          }
          if (error.status === 400) {
            const apiValidations: ApiValidation[] = error.error;

            apiValidations.forEach((validation) => {
              if (this.formGroup.get(validation.PropertyName)) {
                const control = this.formGroup.get(validation.PropertyName);
                if (control) {
                  control.markAsTouched();
                  control.setErrors({ invalid: validation.ErrorMessage });
                }
              } else {
                ///TODO: if we have cross field validation then show the validation error at the top of the screen
                // if we have cross field validation then show the validation error at the top of the screen
                // push general error messages to array this is displayed in a toast or dialog
              }
            });
          }
        }
      });
    }
  }

  removeTag(tag: Tag): void {
    const index = this.assetTagIds.findIndex(_tag => _tag.Id == tag.Id);

    if (index >= 0) {
      this.assetTagIds.splice(index, 1);
    }
    this.assetTags$ = this.assetTags$.pipe(map(tags => {
      return tags.filter(tag => {
        const index = this.assetTagIds.findIndex(_tag => _tag.Id == tag.Id);
        return index < 0
      })
    }));
  }

  ngOnDestroy() { }

  toCamel(o: any) {
    var newO, origKey, newKey, value
    if (o instanceof Array) {
      return o.map((value) => {
        if (typeof value === "object") {
          value = this.toCamel(value)
        }
        return value
      })
    } else {
      newO = {} as any;
      for (origKey in o) {
        if (o.hasOwnProperty(origKey)) {
          newKey = (origKey.charAt(0).toLowerCase() + origKey.slice(1) || origKey).toString()
          value = o[origKey]
          if (value instanceof Array || (value !== null && value.constructor === Object)) {
            value = this.toCamel(value)
          }
          newO[newKey] = value
        }
      }
    }
    return newO
  }

  getImageSrc(assetsUrl: any, assets: any) {
    let imagesrc = '';
    if (assets.Type === 'application/pdf') {
      imagesrc = './assets/images/pdf.png';
    } else if (assets.Type.indexOf('audio') > -1) {
      imagesrc = './assets/images/mp3.jpg';
    } else if (assets.Type.indexOf('video') > -1) {
      imagesrc = './assets/images/mp4.png';
    } else if (assets.Type.indexOf('text') > -1) {
      imagesrc = './assets/images/text.png';
    }
    else {
      imagesrc = assetsUrl + '/' + assets.Detail.FileName;
    }
    return imagesrc;
  }

  openPreviewDialog(): void {
    const dialogRef = this.dialog.open(MediaPreviewComponent, {
      data: { name: this.assetsDetails.AssetName, url: this.assetsDetails.FileName, type: this.assetsDetails.Type },
    });
  }

}
